<template>
  <SoDialog
    ref="remove-payment-review-record-modal"
    v-model="isOpen"
    :card-title="`You're About to Remove ${recordIds.length} Records`"
  >
    <SoForm @submit="removeRecords">
      <p class="text-h6">
        The records you have selected will be removed from this list and can no
        longer be retried. Please confirm by pressing remove.
      </p>
      <div class="mt-5 d-flex justify-end">
        <SoButton title="Cancel" @click="isOpen = false" />
        <SoButton
          :loading="isRemoving"
          title="Remove"
          type="success"
          class="ml-3"
          data-testid="remove-payment-review-record-modal-remove-btn"
          submit
        />
      </div>
    </SoForm>

    <SoAlert
      v-if="error"
      type="error"
      :title="error"
      class="mt-5"
      data-testid="remove-payment-review-record-modal-error"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { PaymentReviewMutator } from "@/mutators/paymentReview/PaymentReviewMutator"
  import { DeletePaymentReviewRecordsResponse } from "@/api/backofficeRest/generated/model"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoForm,
      SoButton,
      SoDialog,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true,
      },
      recordIds: {
        type: Array as PropType<number[]>,
        default: () => [],
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        error: "",
        isRemoving: false,
      }
    },
    computed: {
      isOpen: {
        get(): boolean {
          return this.modelValue
        },
        set(val: boolean) {
          this.$emit("update:modelValue", val)
        },
      },
    },
    methods: {
      async removeRecords() {
        this.isRemoving = true
        try {
          const { mutateAsync } = PaymentReviewMutator.deleteRecordsByIds()
          const response = (await mutateAsync({
            ids: this.recordIds,
          })) as DeletePaymentReviewRecordsResponse

          if (response?.result !== "success") {
            throw new Error("Failed to remove selected records")
          }

          this.$emit("success", true)
          this.isOpen = false
        } catch (e) {
          errorReporter.report(e)
          console.error(e)
          this.error = errorHandler.userMessage(e, "Failed to remove records")
        }

        this.isRemoving = false
      },
    },
  })
</script>
